/**
 * `buildWikiImageExtension` — extends the canonical TipTap `Image`
 * extension with two extra attributes (`attachmentId`,
 * `uploadingBlobUrl`) and binds a React NodeView so the wiki surface
 * can render upload-in-flight spinners + signed-GET resolution +
 * lightbox + error fallback.
 *
 * Why extend rather than replace? Other editor surfaces (cards, comments,
 * chat) keep the default `Image` extension's plain `<img src>` rendering
 * — only the wiki surface needs the upload-aware NodeView. The
 * `extensions.ts` gating is surface-conditional: this extension lands
 * in the extension array only when `surface === 'wiki'`, replacing the
 * default Image entry there.
 *
 * Attribute shape:
 *   - `src` (inherited) — absolute URL string for legacy markdown docs
 *     that embed `![alt](https://…)`.
 *   - `alt` (inherited) — alt text.
 *   - `title` (inherited) — optional title attr.
 *   - `attachmentId` (new) — UUID of the `attachments` row when the
 *     image was uploaded through FU-D presign with parentKind='wiki-image'.
 *     Coexists with `src`: a legacy markdown doc has `src` set and
 *     `attachmentId === null`; a freshly uploaded image has
 *     `attachmentId` set and `src === null` (the NodeView resolves the
 *     URL at render time via `useImagePresignedUrl`).
 *   - `uploadingBlobUrl` (new, transient) — `URL.createObjectURL(file)`
 *     blob URL written by the upload orchestrator at file-pick time so
 *     the NodeView can render the bytes immediately, with a spinner
 *     overlay, while the FU-D round-trip lands in the background. NEVER
 *     serialized — the parseHTML side ignores any `data-uploading-blob-url`
 *     attr and the JSON storage path treats it as in-memory-only.
 *
 * Round-trip safety:
 *   - Markdown:   `attachmentId` doesn't round-trip through markdown
 *                  (markdown's `![alt](url)` shape has no slot for it).
 *                  Legacy markdown docs that store an absolute URL keep
 *                  using `src` exactly as before; this extension's
 *                  parseHTML stays compatible with the default Image's
 *                  HTML output.
 *   - TipTap JSON: `attachmentId` rides along in `attrs.attachmentId`
 *                  losslessly. The wiki surface flips to JSON storage
 *                  on first save (per ADR-031), so any freshly-uploaded
 *                  image saves cleanly as JSON with the attachment
 *                  reference intact.
 *
 * The `uploadingBlobUrl` is deliberately omitted from the parseHTML +
 * renderHTML round-trip — it's a transient render hint, not persisted
 * state. A doc save that races the upload commit cleanup would leave
 * the attr in the saved JSON; the load path tolerates it as a harmless
 * extra attr and the NodeView's effect detects the absence of a live
 * blob URL via a `URL.canParse` + revoke-tracking guard.
 */

import Image from '@tiptap/extension-image'
import { type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'
import { imageMarkdownStorage } from './image-markdown'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

export interface BuildWikiImageExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

/**
 * Returns the wiki-flavored Image extension. The base `Image` extension
 * provides the `setImage` command + the `![alt](src)` input rule; we
 * add `attachmentId` + `uploadingBlobUrl` attrs and bind a custom
 * NodeView.
 */
export function buildWikiImageExtension({ view }: BuildWikiImageExtensionOptions) {
  return Image.extend({
    name: 'image',
    // Guards the copy path against a src-less image — see `image-markdown`.
    addStorage() {
      return { ...this.parent?.(), ...imageMarkdownStorage() }
    },
    // The stock rule only matches `img[src]`, but an attachment-backed
    // image is copied WITHOUT a src (the signed URL expires; the paste
    // re-links by id), so its clipboard HTML would never parse back into a
    // node. The extra rule takes a src-less `<img>` only when it carries
    // one of our attachment ids — an arbitrary src-less image from another
    // site still parses to nothing.
    parseHTML() {
      return [
        ...(this.parent?.() ?? []),
        {
          tag: 'img[data-attachment-id]',
          getAttrs: (el: HTMLElement) =>
            !el.getAttribute('src') && isUuid(el.getAttribute('data-attachment-id')) ? null : false,
        },
      ]
    },

    addAttributes() {
      const parent = this.parent?.() ?? {}
      return {
        ...parent,
        attachmentId: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-attachment-id')
            return isUuid(raw) ? raw : null
          },
          renderHTML: (attrs: { attachmentId: string | null }) =>
            attrs.attachmentId ? { 'data-attachment-id': attrs.attachmentId } : {},
        },
        uploadingBlobUrl: {
          // In-memory-only — never round-trips through HTML / markdown
          // / JSON serialization. Renders the NodeView's "uploading"
          // branch via the live blob URL captured at file-pick time.
          default: null as string | null,
          parseHTML: () => null,
          renderHTML: () => ({}),
        },
        uploadingFailed: {
          // In-memory-only — flips the uploading placeholder into the
          // error branch (retry / dismiss) when the upload failed.
          // Never serialized: the flush-time strip removes the whole
          // node (uploadingBlobUrl set + attachmentId null) regardless.
          default: false,
          parseHTML: () => false,
          renderHTML: () => ({}),
        },
      }
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },
  })
}

export { isUuid as isWikiImageAttachmentId }
